import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import {
  body16MainStyles,
  body16BoldStyles,
  body14Styles,
  paragraphStyles,
} from "@constants/typography"
import ImageThumbnails, { ImageWrapper } from "@components/ImageThumbnails"
import Stars from "@components/Stars"
import { YotpoReview } from "../../reviews/types"
import { getIn } from "@utils/index"
import {
  DesktopOnlyContainer,
  DesktopOnly,
  MobileAndTabletOnly,
} from "@utils/MediaQuery"
import moment from "moment"
import _ from "lodash-es"

const Row = styled.div`
  flex-direction: row;
  align-items: center;
  display: flex;
`

const Col = styled.div`
  flex-direction: column;
  display: flex;
`

const Container = styled.div`
  flex-direction: column;
  display: flex;

  @media ${breakpoints.laptop} {
    flex-direction: row;

    ${DesktopOnlyContainer} {
      display: flex;
    }
  }
`

const Title = styled.div`
  ${body16BoldStyles}
  margin-bottom: 8px;
  color: ${colors.black};
`

const Body = styled.div`
  ${paragraphStyles}
  width: 100%;
  margin-top: 16px;
  color: ${colors.mediumGray};

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    margin-top: 0;
    opacity: 0.5;
    color: ${colors.black};
  }
`

const Rating = styled(Stars)`
  @media ${breakpoints.laptop} {
    margin-bottom: 16px;
  }
`

const Author = styled.div`
  ${body14Styles}
  flex-wrap: wrap;
  margin-left: 16px;
  opacity: 0.7;
  color: ${colors.mediumDarkGray};

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    width: calc(1.25 / 12 * 100vw);
    margin-left: 0;
    margin-right: 16px;
    color: ${colors.mediumDarkGray};
  }
`

const Thumbnails = styled.div`
  margin-top: 16px;

  @media ${breakpoints.laptop} {
    ${ImageWrapper} {
      margin-right: 16px;
      margin-bottom: 16px;
    }
  }
`

const Spacer = styled.div`
  width: 100%;
  height: 12px;
`

const formatName = (name: string) => {
  const startCased = _.startCase(name)
  const words = _.words(startCased)
  const lastWord = _.last(words)

  if (words.length > 1 && lastWord) {
    const lastWordIndex = _.lastIndexOf(words, lastWord)
    const firstLetter = lastWord.charAt(0)
    words[lastWordIndex] = `${firstLetter}.`
    return words.join(" ")
  }
  return name
}

interface ReviewProps {
  data: YotpoReview
  className?: string
  onClickImage?: (id) => void
}

const Review: React.FC<ReviewProps> = (props) => {
  const reviewer = getIn(props.data, "user.display_name")
  const createDate = getIn(props.data, "created_at")
  const title = getIn(props.data, "title")
  const body = getIn(props.data, "content")
  const rating = parseInt(getIn(props.data, "score"))

  const formattedReviewer = formatName(reviewer)
  const formattedCreateDate = moment(createDate).format("M/D/YY")

  const [parsedTitle, setParsedTitle] = React.useState(title)
  const [parsedBody, setParsedBody] = React.useState(body)

  React.useEffect(() => {
    if (DOMParser) {
      const decodeHtml = (str) => {
        const doc = new DOMParser().parseFromString(str, "text/html")
        return doc.documentElement.textContent
      }

      setParsedTitle(decodeHtml(title))
      setParsedBody(decodeHtml(body))
    }
  }, [title, body])

  const onClickImage = (id) => {
    props.onClickImage && props.onClickImage(id)
  }

  let images

  if (props.data.images_data) {
    images = props.data.images_data.map((image: any) => ({
      id: image.id,
      review: props.data.id,
      alt: props.data.title,
      image: image.original_url,
      thumbnail: image.thumb_url,
      onClick: () => {
        onClickImage(image.id)
      },
    }))
  }

  return (
    <Container className={props.className}>
      <DesktopOnly>
        <Col>
          <Rating stars={rating} />
          <Author>{`${formattedReviewer} on ${formattedCreateDate}`}</Author>
        </Col>
        <Col>
          <Title>{parsedTitle}</Title>
          <Body>{parsedBody}</Body>
          {images && (
            <Thumbnails>
              <ImageThumbnails images={images} imagesToShow={images.length} />
            </Thumbnails>
          )}
        </Col>
      </DesktopOnly>
      <MobileAndTabletOnly>
        <Row>
          <Title>{parsedTitle}</Title>
        </Row>
        <Row>
          <Rating stars={rating} />
          <Author>{`${formattedReviewer} on ${formattedCreateDate}`}</Author>
        </Row>
        <Row>
          <Body>{parsedBody}</Body>
        </Row>
        <Row>
          {images && (
            <Thumbnails>
              <ImageThumbnails images={images} imagesToShow={images.length} />
            </Thumbnails>
          )}
          {!images && <Spacer />}
        </Row>
      </MobileAndTabletOnly>
    </Container>
  )
}

export default Review
